/**
 * Sanitize AI-generated text for WhatsApp copy-paste.
 * Converts Markdown-only syntax to WhatsApp-native syntax, strips artifacts,
 * and normalizes whitespace so what the user copies renders correctly in WhatsApp.
 *
 * Idempotent: applying twice yields the same result.
 */
export function sanitizeWhatsAppText(input: string): string {
  if (!input) return "";
  let text = input.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  // Remove fenced code blocks entirely (keep inner text)
  text = text.replace(/```[a-zA-Z0-9]*\n?([\s\S]*?)```/g, "$1");
  // Remove inline backticks
  text = text.replace(/`([^`]+)`/g, "$1");

  // Images: ![alt](url) -> url
  text = text.replace(/!\[[^\]]*\]\(([^)]+)\)/g, "$1");
  // Links: [text](url) -> text (url); if text == url, keep url alone
  text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label: string, url: string) =>
    label.trim() === url.trim() ? url : `${label} (${url})`,
  );

  // Bold: **x** or __x__ -> *x*
  text = text.replace(/\*\*([^\n*]+?)\*\*/g, "*$1*");
  text = text.replace(/__([^\n_]+?)__/g, "*$1*");

  // Process line-by-line for prefixes and trailing spaces
  const lines = text.split("\n").map((line) => {
    let l = line;
    // Strip Markdown headings: "## Titre" -> "Titre"
    l = l.replace(/^\s{0,3}#{1,6}\s+/, "");
    // Strip blockquotes: "> texte" -> "texte"
    l = l.replace(/^\s{0,3}>+\s?/, "");
    // Strip standalone horizontal rules
    if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(l)) l = "";
    // Trim trailing spaces
    l = l.replace(/[ \t]+$/g, "");
    return l;
  });
  text = lines.join("\n");

  // Remove common AI preambles on the first non-empty line
  text = text.replace(
    /^\s*(?:voici (?:le|un|votre|ton)\s+message[^\n:]*:?\s*\n+|here'?s (?:the|your|a)\s+message[^\n:]*:?\s*\n+)/i,
    "",
  );

  // Collapse 3+ newlines -> 2 (max one blank line between paragraphs)
  text = text.replace(/\n{3,}/g, "\n\n");

  // Trim leading/trailing blank lines
  text = text.replace(/^\s+|\s+$/g, "");

  return text;
}
